/**
 * Simple in-memory cache with TTL support
 */
class MemoryCache {
    private cache = new Map<string, { data: any; expires: number }>()
    private defaultTTL = 5 * 60 * 1000 // 5 minutes

    set(key: string, data: any, ttl?: number): void {
        const expires = Date.now() + (ttl || this.defaultTTL)
        this.cache.set(key, { data, expires })
    }

    get<T = any>(key: string): T | null {
        const item = this.cache.get(key)

        if (!item) {
            return null
        }

        if (Date.now() > item.expires) {
            this.cache.delete(key)
            return null
        }

        return item.data
    }

    has(key: string): boolean {
        const item = this.cache.get(key)

        if (!item) {
            return false
        }

        if (Date.now() > item.expires) {
            this.cache.delete(key)
            return false
        }

        return true
    }

    delete(key: string): boolean {
        return this.cache.delete(key)
    }

    clear(): void {
        this.cache.clear()
    }

    size(): number {
        // Clean expired items first
        this.cleanExpired()
        return this.cache.size
    }

    private cleanExpired(): void {
        const now = Date.now()
        Array.from(this.cache.entries()).forEach(([key, item]) => {
            if (now > item.expires) {
                this.cache.delete(key)
            }
        })
    }
}

// Global cache instance
export const memoryCache = new MemoryCache()

/**
 * Browser storage cache with fallback to memory
 */
class BrowserCache {
    private fallbackCache = new MemoryCache()
    private storageAvailable = typeof window !== 'undefined' && window.localStorage

    set(key: string, data: any, ttl: number = 5 * 60 * 1000): void {
        const item = {
            data,
            expires: Date.now() + ttl
        }

        if (this.storageAvailable) {
            try {
                localStorage.setItem(`cache_${key}`, JSON.stringify(item))
                return
            } catch (error) {
                console.warn('Failed to set localStorage cache:', error)
            }
        }

        // Fallback to memory cache
        this.fallbackCache.set(key, data, ttl)
    }

    get<T = any>(key: string): T | null {
        if (this.storageAvailable) {
            try {
                const stored = localStorage.getItem(`cache_${key}`)
                if (stored) {
                    const item = JSON.parse(stored)

                    if (Date.now() > item.expires) {
                        localStorage.removeItem(`cache_${key}`)
                        return null
                    }

                    return item.data
                }
            } catch (error) {
                console.warn('Failed to get localStorage cache:', error)
            }
        }

        // Fallback to memory cache
        return this.fallbackCache.get<T>(key)
    }

    has(key: string): boolean {
        if (this.storageAvailable) {
            try {
                const stored = localStorage.getItem(`cache_${key}`)
                if (stored) {
                    const item = JSON.parse(stored)

                    if (Date.now() > item.expires) {
                        localStorage.removeItem(`cache_${key}`)
                        return false
                    }

                    return true
                }
            } catch (error) {
                console.warn('Failed to check localStorage cache:', error)
            }
        }

        // Fallback to memory cache
        return this.fallbackCache.has(key)
    }

    delete(key: string): boolean {
        let deleted = false

        if (this.storageAvailable) {
            try {
                localStorage.removeItem(`cache_${key}`)
                deleted = true
            } catch (error) {
                console.warn('Failed to delete localStorage cache:', error)
            }
        }

        // Also delete from fallback cache
        return this.fallbackCache.delete(key) || deleted
    }

    clear(): void {
        if (this.storageAvailable) {
            try {
                const keys = Object.keys(localStorage).filter(key => key.startsWith('cache_'))
                keys.forEach(key => localStorage.removeItem(key))
            } catch (error) {
                console.warn('Failed to clear localStorage cache:', error)
            }
        }

        this.fallbackCache.clear()
    }
}

// Global browser cache instance
export const browserCache = new BrowserCache()

/**
 * Cache decorator for functions
 */
export function cached<T extends (...args: any[]) => any>(
    fn: T,
    options: {
        ttl?: number
        keyGenerator?: (...args: Parameters<T>) => string
        cache?: MemoryCache | BrowserCache
    } = {}
): T {
    const {
        ttl = 5 * 60 * 1000, // 5 minutes
        keyGenerator = (...args) => JSON.stringify(args),
        cache = memoryCache
    } = options

    return ((...args: Parameters<T>) => {
        const key = `fn_${fn.name}_${keyGenerator(...args)}`

        // Check cache first
        if (cache.has(key)) {
            return cache.get(key)
        }

        // Execute function and cache result
        const result = fn(...args)

        // Handle promises
        if (result instanceof Promise) {
            return result.then((data) => {
                cache.set(key, data, ttl)
                return data
            }).catch((error) => {
                // Don't cache errors
                throw error
            })
        }

        // Cache synchronous result
        cache.set(key, result, ttl)
        return result
    }) as T
}

/**
 * React hook for cached data fetching
 */
export function useCachedData<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: {
        ttl?: number
        cache?: MemoryCache | BrowserCache
        fallback?: T
    } = {}
) {
    const {
        ttl = 5 * 60 * 1000,
        cache = browserCache,
        fallback
    } = options

    // Check cache first
    const cached = cache.get<T>(key)
    if (cached) {
        return { data: cached, loading: false, error: null }
    }

    // If we have a fallback, use it while loading
    if (fallback) {
        // Fetch in background
        fetcher()
            .then(data => cache.set(key, data, ttl))
            .catch(error => console.warn('Background fetch failed:', error))

        return { data: fallback, loading: false, error: null }
    }

    // No cache, no fallback - need to fetch
    return { data: null, loading: true, error: null }
}

/**
 * Cache invalidation utilities
 */
export const cacheInvalidation = {
    /**
     * Invalidate cache entries by pattern
     */
    invalidatePattern(pattern: RegExp, cache: MemoryCache | BrowserCache = memoryCache) {
        if (cache instanceof BrowserCache && typeof window !== 'undefined') {
            try {
                const keys = Object.keys(localStorage).filter(key =>
                    key.startsWith('cache_') && pattern.test(key.substring(6))
                )
                keys.forEach(key => localStorage.removeItem(key))
            } catch (error) {
                console.warn('Failed to invalidate localStorage pattern:', error)
            }
        }

        // For memory cache, we'd need to track keys separately
        // This is a simplified implementation
        cache.clear()
    },

    /**
     * Invalidate all project-related cache
     */
    invalidateProjects() {
        this.invalidatePattern(/^projects/)
    },

    /**
     * Invalidate all experience-related cache
     */
    invalidateExperience() {
        this.invalidatePattern(/^experience/)
    },

    /**
     * Invalidate all blog-related cache
     */
    invalidateBlog() {
        this.invalidatePattern(/^blog/)
    }
}